import { useCallback, useEffect, useRef, useState } from "react";
import { Eye, EyeOff, Zap, AlertTriangle, RefreshCw, Download, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";

function ExportImportPanel({ onAfterImport }) {
  const fileInputRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  async function handleExport() {
    setError("");
    setExporting(true);
    try {
      const { data } = await api.get("/admin/products/export");
      const today = new Date().toISOString().slice(0, 10);
      const filename = `fembot-nex-catalog-export-${today}.json`;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Export failed.");
    } finally {
      setExporting(false);
    }
  }

  function triggerFilePicker() {
    setError("");
    setResult(null);
    fileInputRef.current?.click();
  }

  async function handleFileSelected(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    setResult(null);

    let bundle;
    try {
      const text = await file.text();
      bundle = JSON.parse(text);
    } catch {
      setError("Invalid JSON file. Could not parse contents.");
      return;
    }

    if (!bundle || !Array.isArray(bundle.products) || bundle.products.length === 0) {
      setError("Bundle is missing a non-empty `products` array.");
      return;
    }
    // Default fetch_remote_images to true if not present
    if (typeof bundle.fetch_remote_images !== "boolean") {
      bundle.fetch_remote_images = true;
    }

    const count = bundle.products.length;
    const ok = window.confirm(
      `Import ${count} product${count === 1 ? "" : "s"} into THIS environment? ` +
        `Existing products with matching slugs will be UPDATED (not duplicated). ` +
        `Images will be re-downloaded from the bundle's source URLs and stored locally.`
    );
    if (!ok) return;

    setImporting(true);
    try {
      const { data } = await api.post("/admin/products/bulk-upsert", bundle, {
        timeout: 300000, // 5 min — large bundles with many images can be slow
      });
      setResult(data);
      onAfterImport?.();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Import failed.");
    } finally {
      setImporting(false);
    }
  }

  return (
    <div
      data-testid="drop-manager-sync-panel"
      className="border border-[#2a1a3a] bg-[#0a0316]/50 p-4 md:p-5 mb-6"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <RefreshCw size={18} className="text-[#c084fc] shrink-0 mt-0.5" strokeWidth={1.5} />
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#ff1493]">
            // Catalog Sync · Preview ⇄ Production
          </div>
          <p className="mt-1.5 font-mono text-[11px] leading-relaxed text-[#b4a3c7]">
            Use this to move product data between preview and production. Redeploying code does not
            migrate MongoDB product records.
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={handleExport}
          disabled={exporting || importing}
          data-testid="catalog-export-btn"
          className="btn-outline !py-2 !px-4 !text-[11px]"
        >
          <Download size={12} strokeWidth={1.5} />
          {exporting ? "Exporting…" : "Export catalog → bundle"}
        </button>
        <button
          type="button"
          onClick={triggerFilePicker}
          disabled={exporting || importing}
          data-testid="catalog-import-btn"
          className="btn-primary !py-2 !px-4 !text-[11px]"
        >
          <Upload size={12} strokeWidth={1.5} />
          {importing ? "Importing…" : "Import bundle"}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json,.json"
          onChange={handleFileSelected}
          className="hidden"
          data-testid="catalog-import-file"
        />
      </div>

      {importing && (
        <div
          data-testid="catalog-import-progress"
          className="mt-4 font-mono text-[11px] tracking-[0.22em] uppercase text-[#c084fc] animate-pulse"
        >
          // mirroring images and upserting products — this can take a minute…
        </div>
      )}

      {error && (
        <div
          data-testid="catalog-sync-error"
          className="mt-4 border-l-2 border-[#ff3b30] pl-3 font-mono text-[11px] leading-relaxed text-[#ff3b30]"
        >
          // {error}
        </div>
      )}

      {result && (
        <div
          data-testid="catalog-import-result"
          className="mt-4 border border-[#00ff41]/40 bg-[#001a05]/40 p-3"
        >
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#00ff41]">
            // Import Result
          </div>
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3 font-mono text-[11px]">
            <div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-[#7a6a90]">Created</div>
              <div className="text-white text-base mt-0.5" data-testid="result-created-count">
                {result.created?.length ?? 0}
              </div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-[#7a6a90]">Updated</div>
              <div className="text-white text-base mt-0.5" data-testid="result-updated-count">
                {result.updated?.length ?? 0}
              </div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-[#7a6a90]">
                Image errors
              </div>
              <div
                className={`text-base mt-0.5 ${
                  (result.image_errors?.length ?? 0) > 0 ? "text-[#ff3b30]" : "text-white"
                }`}
                data-testid="result-image-errors-count"
              >
                {result.image_errors?.length ?? 0}
              </div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-[#7a6a90]">
                Total after
              </div>
              <div className="text-white text-base mt-0.5" data-testid="result-total-after">
                {result.total_after ?? "—"}
              </div>
            </div>
          </div>
          {(result.created?.length ?? 0) > 0 && (
            <details className="mt-3 font-mono text-[11px] text-[#b4a3c7]">
              <summary className="cursor-pointer text-[#7a6a90]">
                created slugs ({result.created.length})
              </summary>
              <ul className="mt-1 pl-4">
                {result.created.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </details>
          )}
          {(result.image_errors?.length ?? 0) > 0 && (
            <details className="mt-2 font-mono text-[11px] text-[#ff3b30]">
              <summary className="cursor-pointer">
                image errors ({result.image_errors.length})
              </summary>
              <ul className="mt-1 pl-4">
                {result.image_errors.slice(0, 20).map((e, i) => (
                  <li key={i}>
                    {e.slug}: {e.url}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

export default function DropManager() {
  const [drop, setDrop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError("");
    try {
      const { data } = await api.get("/admin/drops/001");
      setDrop(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Failed to load drop.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function toggleOne(p) {
    const next = !(p.published !== false);
    try {
      const { data } = await api.patch(`/admin/products/${p.slug}`, { published: next });
      setDrop((d) => ({
        ...d,
        products: d.products.map((it) => (it.slug === p.slug ? data : it)),
        published_count: d.products.reduce(
          (acc, it) => acc + ((it.slug === p.slug ? data : it).published !== false ? 1 : 0),
          0
        ),
        hidden_count: d.products.reduce(
          (acc, it) => acc + ((it.slug === p.slug ? data : it).published === false ? 1 : 0),
          0
        ),
      }));
      setFlash(`${p.name} ${data.published ? "published" : "hidden"}.`);
      setTimeout(() => setFlash(""), 2200);
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail) || "Toggle failed.");
    }
  }

  async function batch(action) {
    const isPublish = action === "publish";
    const verb = isPublish ? "publish" : "hide";
    const message = isPublish
      ? `Publish ALL ${drop.products_count} Drop 001 products? They will become visible on Shop, Drop 001, and product URLs immediately.`
      : `Hide ALL ${drop.products_count} Drop 001 products? They will disappear from Shop, Drop 001, related rails, and product URLs immediately.`;
    if (!window.confirm(message)) return;
    setBusy(true);
    try {
      await api.post(`/admin/drops/001/${isPublish ? "publish-all" : "hide-all"}`);
      await refresh();
      setFlash(`All Drop 001 products ${isPublish ? "published" : "hidden"}.`);
      setTimeout(() => setFlash(""), 2500);
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail) || `Batch ${verb} failed.`);
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="font-mono text-xs tracking-[0.2em] uppercase text-[#7a6a90]">// loading drop...</div>
    );
  }
  if (error) {
    return (
      <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#ff3b30]">// {error}</div>
    );
  }

  return (
    <div data-testid="drop-manager">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#ff1493]">
            // Drop {drop.code} · {drop.status}
          </div>
          <h2 className="mt-2 font-display text-2xl md:text-4xl tracking-tight">
            Drop {drop.code}: <span className="text-[#ff1493]">{drop.name}</span>
          </h2>
          <p className="mt-3 font-mono text-[11px] tracking-[0.2em] uppercase text-[#b4a3c7]">
            Launch · {new Date(drop.launch_at).toLocaleString()}
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          className="btn-ghost"
          aria-label="Refresh"
          data-testid="drop-manager-refresh"
        >
          <RefreshCw size={12} strokeWidth={1.5} /> Refresh
        </button>
      </div>

      <ExportImportPanel onAfterImport={refresh} />

      {/* Stat tiles */}
      <div className="grid grid-cols-3 gap-px bg-[#2a1a3a] border border-[#2a1a3a] mb-6">
        {[
          ["Total", drop.products_count, "text-white"],
          ["Published", drop.published_count, "text-[#00ff41]"],
          ["Hidden", drop.hidden_count, "text-[#7a6a90]"],
        ].map(([k, v, color]) => (
          <div key={k} className="bg-[#050010] p-5">
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#7a6a90]">{k}</div>
            <div className={`mt-2 font-display text-3xl md:text-4xl tracking-tight ${color}`}>{v}</div>
          </div>
        ))}
      </div>

      {/* Batch actions */}
      <div className="border border-[#2a1a3a] bg-[#0a0316]/50 p-4 md:p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <AlertTriangle size={18} className="text-[#ff1493] shrink-0" strokeWidth={1.5} />
          <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#b4a3c7] leading-relaxed">
            Batch actions flip every product in this drop. Confirmation required.
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            type="button"
            data-testid="drop-manager-publish-all"
            disabled={busy}
            onClick={() => batch("publish")}
            className="btn-primary !py-2 !px-4 !text-[11px]"
          >
            <Zap size={12} strokeWidth={1.5} />
            Publish All
          </button>
          <button
            type="button"
            data-testid="drop-manager-hide-all"
            disabled={busy}
            onClick={() => batch("hide")}
            className="btn-outline !py-2 !px-4 !text-[11px]"
          >
            <EyeOff size={12} strokeWidth={1.5} />
            Hide All
          </button>
        </div>
      </div>

      {flash && (
        <div data-testid="drop-manager-flash" className="mb-4 font-mono text-[11px] tracking-[0.22em] uppercase text-[#00ff41]">
          // {flash}
        </div>
      )}

      {/* Product table */}
      <div className="border border-[#2a1a3a] overflow-x-auto">
        <table className="min-w-full text-sm" data-testid="drop-manager-table">
          <thead>
            <tr className="bg-[#06000e]">
              {["Title", "Slug", "Category", "Tier", "Price", "Status Badge", "Visibility"].map((h) => (
                <th
                  key={h}
                  className="text-left font-mono text-[10px] tracking-[0.22em] uppercase text-[#ff1493] px-3 py-3 border-b border-[#2a1a3a]"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {drop.products.map((p) => {
              const isPublished = p.published !== false;
              return (
                <tr
                  key={p.slug}
                  data-testid={`drop-manager-row-${p.slug}`}
                  className={`border-b border-[#1a0a2a] last:border-b-0 hover:bg-[#0a0316]/60 ${
                    !isPublished ? "opacity-60" : ""
                  }`}
                >
                  <td className="px-3 py-3 align-middle">
                    <div className="font-display text-sm leading-tight">{p.name}</div>
                  </td>
                  <td className="px-3 py-3 align-middle font-mono text-[11px] tracking-[0.1em] text-[#7a6a90]">
                    {p.slug}
                  </td>
                  <td className="px-3 py-3 align-middle font-mono text-[11px] tracking-[0.1em] uppercase text-[#c084fc]">
                    {p.category}
                    {p.subcategory && (
                      <div className="text-[10px] text-[#7a6a90] mt-0.5">{p.subcategory}</div>
                    )}
                  </td>
                  <td className="px-3 py-3 align-middle font-mono text-[10px] tracking-[0.18em] uppercase">
                    <span className={p.tier === "contraband" ? "text-[#ff3b30]" : "text-[#c084fc]"}>
                      {p.tier}
                    </span>
                  </td>
                  <td className="px-3 py-3 align-middle font-mono text-[12px] text-white">
                    {p.price ? `$${p.price}` : "—"}
                  </td>
                  <td className="px-3 py-3 align-middle font-mono text-[10px] tracking-[0.18em] uppercase text-[#b4a3c7]">
                    {p.status_badge || p.status}
                  </td>
                  <td className="px-3 py-3 align-middle">
                    <button
                      type="button"
                      data-testid={`drop-manager-toggle-${p.slug}`}
                      onClick={() => toggleOne(p)}
                      aria-pressed={isPublished}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 border font-mono text-[10px] tracking-[0.2em] uppercase transition-colors ${
                        isPublished
                          ? "border-[#00ff41]/50 text-[#00ff41] hover:border-[#00ff41]"
                          : "border-[#7a6a90] text-[#7a6a90] hover:border-white hover:text-white"
                      }`}
                    >
                      {isPublished ? (
                        <>
                          <Eye size={11} strokeWidth={1.5} /> Published
                        </>
                      ) : (
                        <>
                          <EyeOff size={11} strokeWidth={1.5} /> Hidden
                        </>
                      )}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
